import { seriesKey } from '../../../shared/utils/vendor-series-key.util';
import type { SeriesObservation } from '../observation/observation';
import type { MeasurementFilter } from './measurement';

/**
 * Filters observations to those on any of the listed channels.
 * No-op when channels is absent or empty (all channels pass).
 */
function filterByChannels(
  obs: readonly SeriesObservation[],
  channels: MeasurementFilter['channels']
): readonly SeriesObservation[] {
  if (channels === undefined || channels.length === 0) return obs;
  const allowed = new Set(channels);
  return obs.filter((o) => allowed.has(o.channel));
}

/**
 * Filters observations to those matching the allowed vendor-key set.
 * No-op when vendors is absent or empty (show all).
 * Vendor keys are seriesKey(vendor, channel) values ('apoteket', 'apoteket-retail', ...).
 */
function filterByVendors(
  obs: readonly SeriesObservation[],
  vendors: readonly string[] | undefined
): readonly SeriesObservation[] {
  if (vendors === undefined || vendors.length === 0) return obs;
  const allowed = new Set(vendors);
  return obs.filter((o) => allowed.has(seriesKey(o.vendor, o.channel)));
}

/**
 * Applies filter.channels then filter.vendors (intersection: observation must pass both).
 * Channel and vendor filters compose by intersection — if vendors encode a specific channel
 * (e.g. 'apoteket-retail' for in-store) that contradicts the channel filter, no observations
 * pass, which is the correct and transparent result.
 */
export function filterObservations(
  obs: readonly SeriesObservation[],
  filter: MeasurementFilter
): readonly SeriesObservation[] {
  return filterByVendors(filterByChannels(obs, filter.channels), filter.vendors);
}
